import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Pagination,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import DownloadIcon from '@mui/icons-material/Download'
import CleaningServicesIcon from '@mui/icons-material/CleaningServices'
import CampaignIcon from '@mui/icons-material/Campaign'
import SearchIcon from '@mui/icons-material/Search'
import {
  notificationService,
  NotificationChannel,
  NotificationDeliveryStatus,
  NotificationDto,
  UserRole,
} from '../api/adminServices'
import { useLocalizedLabel } from '../utils/getLocalizedLabel'
import { ResizableTableCell } from '../components/ResizableTable'
import { exportCsv } from '../utils/exportCsv'
import { ALL_USER_ROLES } from '../utils/userRoles'
import { useToast } from '../components/useToast'

type TabKey = 'history' | 'broadcast'

const PAGE_SIZE = 20

const CHANNELS: NotificationChannel[] = ['PUSH', 'SMS', 'EMAIL', 'WHATSAPP']
const DELIVERY_STATUSES: NotificationDeliveryStatus[] = [
  'NEW',
  'QUEUED',
  'SENT',
  'DELIVERED',
  'FAILED',
  'ARCHIVED',
]
const LOCALES = [
  { value: '', label: 'Any locale' },
  { value: 'EN', label: 'English' },
  { value: 'HE', label: 'Hebrew' },
  { value: 'AR', label: 'Arabic' },
  { value: 'RU', label: 'Russian' },
]

function deliveryColor(
  s?: string
): 'success' | 'info' | 'warning' | 'error' | 'default' {
  if (s === 'DELIVERED') return 'success'
  if (s === 'SENT' || s === 'QUEUED') return 'info'
  if (s === 'NEW') return 'warning'
  if (s === 'FAILED') return 'error'
  return 'default'
}

function HistoryTab() {
  const [page, setPage] = useState(0)
  const [channel, setChannel] = useState<string>('')
  const [deliveryStatus, setDeliveryStatus] = useState<string>('')
  const [userId, setUserId] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [search, setSearch] = useState('')

  const queryClient = useQueryClient()

  const params = useMemo(
    () => ({
      page,
      size: PAGE_SIZE,
      userId: userId.trim() || undefined,
      channel: (channel || undefined) as NotificationChannel | undefined,
      deliveryStatus: (deliveryStatus || undefined) as NotificationDeliveryStatus | undefined,
      from: from ? `${from}T00:00:00` : undefined,
      to: to ? `${to}T23:59:59` : undefined,
    }),
    [page, userId, channel, deliveryStatus, from, to]
  )

  const { data, isLoading } = useQuery({
    queryKey: ['admin-notifications', params],
    queryFn: () => notificationService.getAll(params),
  })

  const rows: NotificationDto[] = useMemo(() => data?.content ?? [], [data])
  const totalPages: number = data?.totalPages ?? 0

  const visibleRows = useMemo(() => {
    if (!search) return rows
    const q = search.toLowerCase()
    return rows.filter(
      (r) =>
        r.id.toLowerCase().includes(q) ||
        r.title?.toLowerCase().includes(q) ||
        r.message?.toLowerCase().includes(q) ||
        r.userId.toLowerCase().includes(q)
    )
  }, [rows, search])

  const handleExport = () => {
    exportCsv<NotificationDto>(
      `notifications-${new Date().toISOString().slice(0, 10)}`,
      [
        { header: 'ID', field: 'id' },
        { header: 'User', field: 'userId' },
        { header: 'Channel', field: 'channel' },
        { header: 'Title', field: 'title' },
        { header: 'Message', field: 'message' },
        { header: 'Subject Type', field: 'subjectType' },
        { header: 'Priority', field: 'priority' },
        { header: 'Status', field: 'status' },
        { header: 'Delivery', field: 'deliveryStatus' },
        { header: 'Sent At', field: 'sentAt' },
        { header: 'Delivered At', field: 'deliveredAt' },
        { header: 'Read At', field: 'readAt' },
      ],
      visibleRows
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
        >
          <TextField
            select
            size="small"
            label="Channel"
            value={channel}
            onChange={(e) => {
              setChannel(e.target.value)
              setPage(0)
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All channels</MenuItem>
            {CHANNELS.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Delivery status"
            value={deliveryStatus}
            onChange={(e) => {
              setDeliveryStatus(e.target.value)
              setPage(0)
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {DELIVERY_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="User ID"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            sx={{ minWidth: 220 }}
          />
          <TextField
            size="small"
            type="date"
            label="From"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value)
              setPage(0)
            }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={to}
            onChange={(e) => {
              setTo(e.target.value)
              setPage(0)
            }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            placeholder="Search id / title / message"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 240, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Export current view as CSV">
            <IconButton onClick={handleExport}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton
              onClick={() =>
                queryClient.invalidateQueries({ queryKey: ['admin-notifications'] })
              }
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px" sx={{ flex: 1, minHeight: 0 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Table size="small" sx={{ height: visibleRows.length === 0 ? '100%' : undefined }}>
            <TableHead>
              <TableRow>
                <ResizableTableCell>User</ResizableTableCell>
                <ResizableTableCell>Channel</ResizableTableCell>
                <ResizableTableCell>Title</ResizableTableCell>
                <ResizableTableCell>Message</ResizableTableCell>
                <ResizableTableCell>Delivery</ResizableTableCell>
                <ResizableTableCell>Read</ResizableTableCell>
                <ResizableTableCell>Sent</ResizableTableCell>
                <ResizableTableCell>Delivered</ResizableTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ verticalAlign: 'middle' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No notifications match the current filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {visibleRows.map((n) => (
                <TableRow key={n.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    <Tooltip title={n.userId}>
                      <span>{n.userId.substring(0, 12)}…</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip label={n.channel} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 220,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {n.title || '—'}
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 260,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Tooltip title={n.message || ''}>
                      <span>{n.message || '—'}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={n.deliveryStatus || '—'}
                      size="small"
                      color={deliveryColor(n.deliveryStatus)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={n.status || '—'}
                      size="small"
                      color={n.status === 'READ' ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {n.sentAt ? new Date(n.sentAt).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell>
                    {n.deliveredAt ? new Date(n.deliveredAt).toLocaleString() : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={(_e, v) => setPage(v - 1)}
            color="primary"
          />
        </Box>
      )}
    </Box>
  )
}

function BroadcastTab() {
  const getLocalizedLabel = useLocalizedLabel()
  const toast = useToast()

  const [channel, setChannel] = useState<NotificationChannel>('PUSH')
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [template, setTemplate] = useState('admin_broadcast')
  const [accountId, setAccountId] = useState('')
  const [locale, setLocale] = useState('')
  const [roles, setRoles] = useState<UserRole[]>([])
  const [confirmOpen, setConfirmOpen] = useState(false)

  const broadcastMutation = useMutation({
    mutationFn: () =>
      notificationService.broadcast({
        channel,
        title: title.trim() || undefined,
        message: message.trim() || undefined,
        template: template.trim(),
        accountId: accountId.trim() || undefined,
        locale: locale || undefined,
        roles: roles.length > 0 ? roles : undefined,
      }),
    onSuccess: (result) => {
      const sent = result?.sent ?? 0
      const recipients = result?.recipients ?? 0
      toast.success(`Broadcast sent to ${sent}/${recipients} recipient(s)`)
      setConfirmOpen(false)
      setTitle('')
      setMessage('')
    },
    onError: (e) => toast.errorFrom(e, 'Broadcast failed'),
  })

  const canSubmit = !!channel && !!template.trim()

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Audience
            </Typography>
            <Stack spacing={2}>
              <TextField
                size="small"
                label="Account ID (optional)"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                helperText="Leave blank to target every account"
              />
              <TextField
                select
                size="small"
                label="Locale"
                value={locale}
                onChange={(e) => setLocale(e.target.value)}
              >
                {LOCALES.map((l) => (
                  <MenuItem key={l.value} value={l.value}>
                    {l.label}
                  </MenuItem>
                ))}
              </TextField>
              <FormControl size="small">
                <InputLabel>Roles</InputLabel>
                <Select
                  multiple
                  value={roles}
                  onChange={(e) =>
                    setRoles(
                      (typeof e.target.value === 'string'
                        ? e.target.value.split(',')
                        : e.target.value) as UserRole[]
                    )
                  }
                  input={<OutlinedInput label="Roles" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip
                          key={value}
                          label={getLocalizedLabel('userRole', value) || value}
                          size="small"
                        />
                      ))}
                    </Box>
                  )}
                >
                  {ALL_USER_ROLES.map((role) => (
                    <MenuItem key={role} value={role}>
                      {getLocalizedLabel('userRole', role) || role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Alert severity="info" variant="outlined">
                All criteria are AND-ed. Leave a field blank for "no filter". With
                no filters the broadcast reaches every active user.
              </Alert>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Message
            </Typography>
            <Stack spacing={2}>
              <TextField
                select
                size="small"
                label="Channel *"
                value={channel}
                onChange={(e) => setChannel(e.target.value as NotificationChannel)}
              >
                {CHANNELS.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Template *"
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                helperText="Template id used by the notification provider"
              />
              <TextField
                size="small"
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              <TextField
                size="small"
                label="Message body"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                multiline
                rows={5}
              />
              <Button
                variant="contained"
                startIcon={<CampaignIcon />}
                disabled={!canSubmit || broadcastMutation.isPending}
                onClick={() => setConfirmOpen(true)}
              >
                {broadcastMutation.isPending ? 'Sending…' : 'Send Broadcast'}
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm broadcast</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            <Typography variant="body2">
              You're about to send a <strong>{channel}</strong> notification to every
              active user matching:
            </Typography>
            <ul style={{ marginTop: 4 }}>
              <li>
                Account: <strong>{accountId.trim() || 'any'}</strong>
              </li>
              <li>
                Locale: <strong>{locale || 'any'}</strong>
              </li>
              <li>
                Roles: <strong>{roles.length > 0 ? roles.join(', ') : 'any'}</strong>
              </li>
            </ul>
            <Alert severity="warning">
              This action cannot be undone. Recipients will receive the notification immediately.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CampaignIcon />}
            disabled={broadcastMutation.isPending}
            onClick={() => broadcastMutation.mutate()}
          >
            {broadcastMutation.isPending ? 'Sending…' : 'Confirm Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default function NotificationsPage() {
  const [tab, setTab] = useState<TabKey>('history')
  const [cleanupOpen, setCleanupOpen] = useState(false)
  const toast = useToast()

  const cleanupMutation = useMutation({
    mutationFn: () => notificationService.cleanupExpired(),
    onSuccess: () => {
      toast.success('Expired-notifications cleanup triggered')
      setCleanupOpen(false)
    },
    onError: (e) => toast.errorFrom(e, 'Cleanup failed'),
  })

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Stack direction="row" justifyContent="flex-end" alignItems="center" mb={2}>
        <Tooltip title="Trigger expired-notifications cleanup">
          <Button
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<CleaningServicesIcon />}
            onClick={() => setCleanupOpen(true)}
          >
            Cleanup Expired
          </Button>
        </Tooltip>
      </Stack>

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="History" value="history" />
        <Tab label="Broadcast" value="broadcast" />
      </Tabs>

      {tab === 'history' && <HistoryTab />}
      {tab === 'broadcast' && <BroadcastTab />}

      <Dialog open={cleanupOpen} onClose={() => setCleanupOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Clean up expired notifications</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Triggers the same expired-notifications cleanup the retention scheduler runs.
            This deletes notifications past their retention window. Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCleanupOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<CleaningServicesIcon />}
            disabled={cleanupMutation.isPending}
            onClick={() => cleanupMutation.mutate()}
          >
            {cleanupMutation.isPending ? 'Cleaning…' : 'Cleanup'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
